import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { CodeCustomerToShowTyInversed } from '@teinor/erp/types/company/config/parameter'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { RowMouseEventHandlerParams } from 'react-virtualized'
import SearchBar from '../../../../../../../baseComponents/SearchBar'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import { InputTableURLsTable } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { OnSubmit } from '../../../../../../../forms/TwinForm/types'
import { SetTPVCompleteDataTy, TPVOrderData } from '../../../../../../../specificComponents/inputTableLayouts/order/OrderSellTicketTPVLayout/types'
import { getDictionaryStates } from '../../../../../../../utils/globals/countries'
import twinFetchPost, { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { parsePhoneNumber } from '../../../../../../../utils/globals/phone'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getConfigParam } from '../../../../../../../utils/reducers/getters'
import ModalCreateCustomer from '../../../../Company/CustomerParent/AllCustomers/ModalCreateCustomer'
import ModalChangeSpecificPrices from './ModalChangeSpecificPrices'
import ModalCustomerListingTPV from './ModalCustomerListingTPV'
import '../SCDashboardTPV.sass'

interface CustomerDataTPVDSBProps extends CustomerDataTPVDSBLogicProps { 
    title: string
}

const CustomerDataTPVDSB: React.FC<CustomerDataTPVDSBProps> = ({ completeData, title, ...rest}) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal, handleSelectCustomer, handleOnSubmitCreateCustomer, customerAddress, deleteCustomer } = useCustomerDetailsContentLogic({ completeData, ...rest })
    return (
        <div className='flex-col gap-y-10 flex flex-auto' key={completeData?.header?.id || 'no_ticket'}>
            <div className='flex items-center '>
                <h3 className=''>{title}</h3>
                {!completeData?.header?.readOnly || !completeData?.header?.id ? <div className='ml-auto cursor-pointer text-green-43' onClick={() => setOpenModal({ type: 'create', allRowData: {} })}><TwinTrans transKey='createCustomer'>Crear cliente</TwinTrans></div> : null}
            </div>
            {
                !completeData?.header?.readOnly || !completeData?.header?.id ? 
                    <SearchBar placeholder={t('searchDots', 'Buscar...')} searchValue={''} onChange={() => { }} onFocus={() => setOpenModal({ type: 'edit', allRowData: {} })} />
                : null
            }
            <CustomerProfileDetails customerAddress={customerAddress} completeData={completeData} deleteCustomer={deleteCustomer} />
            {openModal?.type === 'create' ? <ModalCreateCustomer setOpened={() => setOpenModal(null)} onSubmit={handleOnSubmitCreateCustomer}  /> : null}
            {openModal?.type === 'edit' ? <ModalCustomerListingTPV setOpened={() => setOpenModal(null)} onSubmit={() => { }} handleSelectCustomer={handleSelectCustomer} /> : null}
            {openModal?.type === 'delete' ? <ModalChangeSpecificPrices setOpenModal={setOpenModal} setOpened={() => setOpenModal(null)} onSubmit={() => rest.getCompleteData()} allRowData={openModal.allRowData}  /> : null}
        </div>
    )
}

interface CustomerDataTPVDSBLogicProps {
    completeData: TPVOrderData | null
    inputTableURLs: InputTableURLsTable
    setCompleteData: SetTPVCompleteDataTy
    getCompleteData: () => Promise<void>
}
const useCustomerDetailsContentLogic = ({ setCompleteData, inputTableURLs, completeData, getCompleteData }: CustomerDataTPVDSBLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const [customerAddress, setCustomerAddress] = useState<TwinDictionary[] | null>(null)

    const changeCashCustomer = useCallback(async (CustomerId: number) => {
        const result = await twinFetchPostJSON('/api/app/customer/getCustomerInstance', { id: CustomerId })
        if (result) {
            setCompleteData((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.Customer = result
                copyOld.cashRegister['CustomerId'] = CustomerId
                return copyOld
            })
            setOpenModal(null)
        }
    }, [setOpenModal, setCompleteData])

    const changeTicketCustomer = useCallback(async (CustomerId: number | null) => {
        if (completeData?.header?.id) {
            const result = await twinFetchPost(inputTableURLs.updateHeaderData, { id: completeData?.header?.id, CustomerId, WorkingCenterId: completeData?.header?.WorkingCenterId, CurrencyId: completeData?.header?.CurrencyId })
            const resultJSON = await result.json()
            if (resultJSON) {
                setCompleteData((old) => {
                    const copyOld = JSON.parse(JSON.stringify(old))
                    copyOld.header.CustomerId = CustomerId
                    return copyOld
                })
                if (resultJSON?.errors?.[0]?.msg === 'changeSpecificPriceRule' && !completeData?.header?.readOnly) {
                    setOpenModal({ type: 'delete', allRowData: { id: completeData?.header.id } })
                } else {
                    setOpenModal(null)
                }
                await getCompleteData()
            }
        }
    }, [setOpenModal, setCompleteData, getCompleteData, completeData?.header?.id, completeData?.header?.CurrencyId, completeData?.header?.WorkingCenterId, inputTableURLs.updateHeaderData, completeData?.header?.readOnly])

    const deleteCustomer = useCallback(() => {
        if (completeData?.header?.CustomerId) {
            changeTicketCustomer(null)
        } else {
            setCompleteData((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.Customer = {}
                return copyOld
            })
        }
    }, [completeData, setCompleteData, changeTicketCustomer])
    
    const getAllCustomerAddress = useCallback(async (myCustomerId: number) => {
        if (myCustomerId) {
            const items = await twinFetchPostJSON('/api/app/customer/customerAddress/getAllCustomerAddresses', { where: { CustomerId: myCustomerId, 'fiscal_address': true } })
            if (items) {
                setCustomerAddress(items)
            } else {
                setCustomerAddress(null)
            }
        }
    }, [setCustomerAddress])

    const handleSelectCustomer = useCallback(async (CustomerId: number, e: RowMouseEventHandlerParams) => {
        e.event.preventDefault()
        if (!completeData?.header?.id) {
            await changeCashCustomer(CustomerId)
        } else {
            await changeTicketCustomer(CustomerId)
        }
        getAllCustomerAddress(CustomerId)
    }, [changeTicketCustomer, changeCashCustomer, completeData?.header?.id, getAllCustomerAddress])

    const handleOnSubmitCreateCustomer: OnSubmit = useCallback(async (res: any, vals) => {
        const result = await res.json()
        if (!completeData?.header?.id) {
            setCompleteData((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const myCustomer = { ...result, additional_address: vals.additional_address, address: vals.address, postal_code: vals.postal_code, city: vals.city }
                copyOld.Customer = myCustomer
                return copyOld
            })
        } else {
            await changeTicketCustomer(result?.id)
        }
        getAllCustomerAddress(result?.id)
        setOpenModal(null)
    }, [setOpenModal, completeData?.header?.id, changeTicketCustomer, setCompleteData, getAllCustomerAddress])

    useEffect(() => {
        if (completeData?.Customer?.id && completeData?.header?.CustomerId && (completeData?.Customer?.id !== completeData?.header?.CustomerId)) {
            getAllCustomerAddress(completeData?.header?.CustomerId)
        }
    }, [getAllCustomerAddress, completeData?.header?.CustomerId, completeData?.Customer?.id])

    return { openModal, setOpenModal, handleSelectCustomer, handleOnSubmitCreateCustomer, customerAddress, deleteCustomer }
}

interface CustomerProfileDetailsProps {
    customerAddress: TwinDictionary[] | null
    completeData: TPVOrderData | null
    deleteCustomer: () => void
}

const CustomerProfileDetails: React.FC<CustomerProfileDetailsProps> = ({ completeData, deleteCustomer, customerAddress }: CustomerProfileDetailsProps) => {
    const { t } = useTwinTranslation()
    const fieldMemo = useMemo(() => {
        const codeCustomerToShow = getConfigParam('codeCustomerToShow')
        let fieldId: 'id' | 'code' = 'id'
        if (parseInt(String(codeCustomerToShow)) === parseInt(String(CodeCustomerToShowTyInversed['codeCustomer']))) {
            fieldId = 'code'
        }
        return fieldId
    }, [])
    if (completeData?.header?.CustomerId || (completeData?.Customer && Object.keys(completeData?.Customer).length)) {
        const myCustomer = completeData?.header?.Customer || completeData?.Customer
        const idOrCode = myCustomer?.[fieldMemo] ? (myCustomer[fieldMemo] + ' - ') : ''
        const myPhone = []
        if (myCustomer?.mobile) {
            myPhone.push(parsePhoneNumber(myCustomer?.mobile))
        }
        if (myCustomer.phone) {
            myPhone.push(parsePhoneNumber(myCustomer?.phone))
        }
        return (
            <div className='flex flex-col flex-auto h-1'>
                <div className='flex flex-col gap-2 overflow-auto '>
                    <div className='mb-3 flex flex-row flex-wrap w-full justify-between gap-x-10 gap-y-2 items-end'>
                        <span className='block flex-auto medium16'>{idOrCode + (myCustomer?.fiscal_name || (myCustomer?.name + ' ' + (myCustomer.surname || '') ))}</span>
                        {!completeData?.header?.status || !completeData?.header?.readOnly ? <div className='cursor-pointer ml-auto' onClick={deleteCustomer}><TwinIcon icon={faTrashCan} className='w-16 h-16' /></div> : null}
                    </div>
                    {myCustomer?.id_number ?
                        <div className='flex flex-row gap-x-5 whitespace-nowrap items-center'>
                            <span className='block light16'>{t('id_number', 'DNI')}</span>
                            <span className={'block light16'}>{myCustomer?.id_number}</span>
                        </div>
                    : null}
                    <div className='flex flex-col flex-auto light14'>
                        <CustomerAddressData {...{ customer: myCustomer, customerAddress }} />
                        <span>{myPhone.join(' / ')}</span>
                        {myCustomer?.email ? <span className='text-green-21'>{myCustomer?.email}</span> : null}
                        {myCustomer?.ResponsibleEmployee?.fullname_short ?
                            <div className='flex flex-col gap-x-8'>
                                <h4 className='medium16 mb-6'>{t('ourManager', 'Nuestro gestor')}</h4>
                                <div className='light14 twin_elipsis'>{myCustomer?.ResponsibleEmployee?.fullname_short}</div>
                            </div>
                        : null}
                    </div>
                </div>
            </div>
        )
    }
    return (<div className='flex flex-col gap-y-10 text-gray-9D '>
        <TwinTrans transKey='notSelectedCustomer'>No has seleccionado ningún cliente</TwinTrans>
    </div>)
  
}

interface CustomerAddressDataProps {
    customerAddress: TwinDictionary[] | null
}

const CustomerAddressData: React.FC<CustomerAddressDataProps> = ({ customerAddress }) => {
    let mainAdress = customerAddress?.[0]
    if (!mainAdress) {
        return null
    }
    const states = getDictionaryStates(mainAdress?.country)
    const stateName = states[mainAdress?.state]?.name
    return (
        <Fragment>
            {mainAdress?.address ? <span>{mainAdress?.address + ', '}</span> : null}
            {mainAdress?.postal_code ? <span>{mainAdress?.postal_code + ', ' + stateName}</span> : null}
        </Fragment>
    )
}


export default CustomerDataTPVDSB
